// src/Components/CategoryTabs.js
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase/firestore';

const CategoryTabs = ({ selectedCategory, onCategoryChange }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const q = query(collection(db, 'categories'), orderBy('order', 'asc'));
      const querySnapshot = await getDocs(q);

      let cats = querySnapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name,
        order: doc.data().order || 0,
      }));

      // Добавим «Все» с order = -1, если его нет
      if (!cats.some(cat => cat.name.toLowerCase() === 'all')) {
        cats.unshift({ 
          id: 'all', 
          name: 'Все', 
          order: -1 
        });
      }

      cats.sort((a, b) => a.order - b.order);
      
      setCategories(cats);
    } catch (error) {
      console.error('Ошибка загрузки категорий:', error);
      setError('Не удалось загрузить категории');
      setCategories([{ 
        id: 'all', 
        name: 'Все', 
        order: -1 
      }]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleCategoryClick = useCallback((categoryName) => {
    onCategoryChange(categoryName);
  }, [onCategoryChange]);

  // ОСНОВНОЕ ИСПРАВЛЕНИЕ: Добавлен truncate и изменены стили
  const renderedCategories = useMemo(() => {
    return categories.map((cat) => (
      <button
        key={cat.id}
        onClick={() => handleCategoryClick(cat.name)}
        className={`
          whitespace-nowrap 
          px-4 py-2 
          rounded-full 
          text-sm font-medium 
          transition-all duration-200 
          text-center
          flex-shrink-0  /* Важно: не сжимается */
          min-h-[36px]   /* Фиксированная минимальная высота */
          max-w-full     /* Ограничение максимальной ширины */
          overflow-hidden
          truncate        /* Обрезаем текст если не помещается */
          ${selectedCategory === cat.name
            ? 'bg-yellow-400 text-black shadow-md transform scale-105'
            : 'bg-yellow-200 text-gray-700 hover:bg-yellow-300 hover:shadow-sm'
          }
        `}
        aria-label={`Выбрать категорию ${cat.name}`}
        aria-pressed={selectedCategory === cat.name}
        title={cat.name} /* Подсказка при наведении на полный текст */
      >
        {cat.name}
      </button>
    ));
  }, [categories, selectedCategory, handleCategoryClick]);

  if (loading) {
    return (
      <div className="px-4 mt-4">
        <div className="flex gap-2 overflow-x-auto pb-2 no-scrollbar">
          {[...Array(5)].map((_, i) => (
            <div 
              key={i} 
              className="flex-shrink-0 px-4 py-2 rounded-full bg-gray-200 animate-pulse min-w-[80px] h-9"
            />
          ))}
        </div>
      </div>
    );
  }

  if (error && categories.length === 0) {
    return (
      <div className="px-4 mt-4">
        <div className="text-center text-red-500 text-sm py-2">
          {error}
        </div>
        <div className="flex gap-2 overflow-x-auto pb-2">
          <button
            onClick={() => handleCategoryClick('Все')}
            className={`
              whitespace-nowrap 
              px-4 py-2 
              rounded-full 
              text-sm font-medium 
              flex-shrink-0
              truncate
              ${selectedCategory === 'Все'
                ? 'bg-yellow-400 text-black'
                : 'bg-yellow-200 text-gray-700'
              }
            `}
          >
            Все
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 mt-4">
      <div 
        className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-yellow-300 scrollbar-track-gray-100"
        style={{ 
          scrollbarWidth: 'thin',
          scrollbarColor: '#fbbf24 #f3f4f6'
        }}
      >
        {renderedCategories}
      </div>
      
      {selectedCategory !== 'Все' && (
        <div className="mt-2 px-4">
          <p className="text-xs text-gray-500">
            Выбрана категория: <span className="font-semibold text-yellow-600">{selectedCategory}</span>
          </p>
        </div>
      )}
    </div>
  );
};

export default CategoryTabs;